import { useState, useEffect, useRef, useCallback } from "react";
import { Play, Pause } from "lucide-react";

function formatTime(seconds) {
  if (!isFinite(seconds) || isNaN(seconds)) return "0:00";
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
}

export function AudioPlayer({ url }) {
  const audioRef = useRef(new Audio());
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [dragging, setDragging] = useState(false);
  const barRef = useRef(null);

  useEffect(() => {
    const audio = audioRef.current;
    audio.src = url;
    setPlaying(false);
    setCurrentTime(0);
    setDuration(0);

    const onLoaded = () => setDuration(audio.duration);
    const onTime = () => { if (!dragging) setCurrentTime(audio.currentTime); };
    const onEnded = () => { setPlaying(false); setCurrentTime(0); };

    audio.addEventListener("loadedmetadata", onLoaded);
    audio.addEventListener("timeupdate", onTime);
    audio.addEventListener("ended", onEnded);
    return () => {
      audio.pause();
      audio.removeEventListener("loadedmetadata", onLoaded);
      audio.removeEventListener("timeupdate", onTime);
      audio.removeEventListener("ended", onEnded);
      audio.src = "";
    };
  }, [url, dragging]);

  const toggle = () => {
    const audio = audioRef.current;
    if (playing) { audio.pause(); setPlaying(false); }
    else { audio.play(); setPlaying(true); }
  };

  const seekTo = useCallback((clientX) => {
    if (!barRef.current || !duration) return;
    const rect = barRef.current.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    const newTime = ratio * duration;
    audioRef.current.currentTime = newTime;
    setCurrentTime(newTime);
  }, [duration]);

  const onBarClick = (e) => seekTo(e.clientX);

  const onMouseDown = (e) => {
    setDragging(true);
    seekTo(e.clientX);
    const onMove = (ev) => seekTo(ev.clientX);
    const onUp = () => { setDragging(false); window.removeEventListener("mousemove", onMove); window.removeEventListener("mouseup", onUp); };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };

  const progress = duration ? (currentTime / duration) * 100 : 0;

  return (
    <div className="flex items-center gap-2 w-full px-3 py-2 rounded-lg bg-[#4D11F6]/10 text-[#4D11F6]">
      <button
        onClick={toggle}
        data-testid="audio-play-btn"
        className="shrink-0 w-6 h-6 flex items-center justify-center rounded-full hover:bg-[#4D11F6]/20 transition-colors"
      >
        {playing ? <Pause size={12} /> : <Play size={12} />}
      </button>

      <span className="text-xs font-mono shrink-0 w-8 text-right">{formatTime(currentTime)}</span>

      <div
        ref={barRef}
        className="relative flex-1 h-1.5 bg-[#4D11F6]/20 rounded-full cursor-pointer"
        onClick={onBarClick}
        onMouseDown={onMouseDown}
      >
        <div
          className="absolute left-0 top-0 h-full bg-[#4D11F6] rounded-full"
          style={{ width: `${progress}%` }}
        />
        <div
          className="absolute top-1/2 -translate-y-1/2 w-3 h-3 bg-[#4D11F6] rounded-full shadow"
          style={{ left: `calc(${progress}% - 6px)` }}
        />
      </div>

      <span className="text-xs font-mono shrink-0 w-8">{formatTime(duration)}</span>
    </div>
  );
}
